import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Header from "../components/Header";
import PortalBack from "../components/PortalBack";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TIERS = [["Explorer", "3 Leverage Links"], ["Insider", "10 Leverage Links · deal flow"], ["Elite", "30 Leverage Links · private introductions"]];

function Upgrade({ user, onClose }) {
  const [sent, setSent] = useState(null);
  const request = async (tier) => {
    const [first, ...rest] = (user?.name || "Member").split(" ");
    try {
      await api.post("/leads", { first_name: first, last_name: rest.join(" ") || null, email: user.email, interests: [`THE LIST upgrade request: ${tier}`], source_page: "list-portal-upgrade" });
      setSent(tier);
    } catch { setSent("error"); }
  };
  return (
    <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-6" data-testid="upgrade-panel">
      <div className="border border-gold/40 p-6 lg:p-8">
        <div className="flex justify-between items-start gap-4 mb-6">
          <div><p className="eyebrow mb-2">Membership options</p><p className="font-headline italic text-ivory/70">Membership details are shared personally with each invitation.</p></div>
          <button onClick={onClose} data-testid="upgrade-close" className="eyebrow !text-[0.55rem] text-ivory/50 hover:text-gold">Close ×</button>
        </div>
        <div className="grid sm:grid-cols-3 gap-px bg-gold/20 border border-gold/20">
          {TIERS.map(([t, d]) => (
            <div key={t} className="bg-obsidian p-5 flex flex-col gap-3">
              <p className="font-display text-2xl">{t}</p><p className="text-sm text-ivory/60 flex-1">{d}</p>
              <button onClick={() => request(t)} data-testid={`upgrade-request-${t.toLowerCase()}`} className="btn-lux w-fit bg-transparent text-gold hover:bg-gold hover:text-obsidian">Request an upgrade</button>
            </div>
          ))}
        </div>
        {sent && <p className="mt-4 text-sm text-gold" data-testid="upgrade-sent">{sent === "error" ? "Could not send. Please try again." : `Thank you. Your ${sent} request is with Team LUXEWELL.`}</p>}
      </div>
    </section>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [links, setLinks] = useState([]);
  const [ops, setOps] = useState([]);
  const [upgrade, setUpgrade] = useState(false);
  useEffect(() => {
    api.get("/list/leverage").then((r) => setLinks(r.data.filter((l) => l.target_profile))).catch(() => {});
    api.get("/list/opportunities").then((r) => setOps(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (user === false) nav("/login", { replace: true }); }, [user, nav]);

  return (
    <div className="min-h-screen bg-obsidian text-ivory">
      <Header variant="list" active="list" />
      <section className="pt-28 pb-8 px-6 lg:px-10 max-w-7xl mx-auto">
        <PortalBack to="/list" label="Back to The LIST" />
        <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow mb-3">Intelligence Dashboard</p>
            <h1 className="font-display text-3xl lg:text-4xl tracking-tight">{user?.name || "Member"}</h1>
            <p className="text-sm text-ivory/50 mt-1">{user?.list_role || "Insider"} · {user?.membership || "Insider"} Membership</p>
            <p className="text-xs text-ivory/40 mt-2" data-testid="demo-note">Demo network: every profile shown is fictional.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/list/opportunities" data-testid="dash-opportunities" className="btn-lux bg-transparent text-ivory hover:bg-white/5">Live in the Network</Link>
            <button onClick={() => setUpgrade(true)} data-testid="dash-upgrade" className="btn-lux bg-gold text-obsidian hover:bg-ivory">Upgrade</button>
          </div>
        </div>
      </section>
      {upgrade && user && <Upgrade user={user} onClose={() => setUpgrade(false)} />}

      <section className="px-6 lg:px-10 max-w-7xl mx-auto py-6">
        <div className="mb-6"><p className="eyebrow mb-1">Your Leverage Links</p><p className="font-headline italic text-ivory/60">The five relationships with the highest potential leverage — and why.</p></div>
        <div className="space-y-px bg-gold/20 border border-gold/20" data-noreveal>
          {links.map((l) => (
            <Link key={l.id} to={`/list/leverage/${l.id}`} data-testid={`leverage-row-${l.id}`}
              className="group flex items-center gap-5 bg-obsidian p-5 lg:p-6 hover:bg-white/5 transition-colors">
              <img src={l.target_profile.image} alt="" className="w-14 h-14 object-cover grayscale group-hover:grayscale-0 transition-all" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <span className="eyebrow !text-[0.55rem] text-gold whitespace-nowrap">Leverage score {l.score}/100</span>
                  <p className="font-headline text-xl truncate">{l.target_profile.name}</p>
                  <span className="hidden sm:inline eyebrow !text-[0.5rem] text-ivory/40">{l.target_profile.role}</span>
                </div>
                <p className="text-xs text-ivory/55 truncate">{l.leak}</p>
              </div>
              <ArrowUpRight className="text-gold shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={20} />
            </Link>
          ))}
        </div>
      </section>

      <section className="px-6 lg:px-10 max-w-7xl mx-auto py-10 grid lg:grid-cols-3 gap-px bg-gold/20 border border-gold/20 mb-16">
        <div className="bg-obsidian p-6"><p className="eyebrow mb-4">Intelligence Briefing</p><p className="font-headline text-lg leading-snug">Q1 luxury longevity demand is concentrating in Italy and India — supply is lagging in physician-led programming.</p></div>
        <div className="bg-obsidian p-6"><p className="eyebrow mb-4">Deal Flow</p>{ops.slice(0, 2).map((o) => (<p key={o.id} className="text-sm text-ivory/70 border-b border-white/5 py-2">{o.title}</p>))}</div>
        <div className="bg-obsidian p-6"><p className="eyebrow mb-4">Plug the Leak</p><p className="font-headline italic text-xl text-gold leading-snug">Strong property, missing practitioner supply.</p><p className="text-xs text-ivory/50 mt-2">One relationship can change the trajectory.</p></div>
      </section>
    </div>
  );
}
